import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import Env from "../env/Env";

const PAGE_EXPORT_TARGET_CLASS = "pdf-export-target";
const CHART_REDRAW_DELAY_MS = 300;

export const PDF_EXPORT_CLOSE_DROPDOWNS_EVENT = "pdf-export-close-dropdowns";

const waitForLayout = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });

const closeOpenDropdowns = async () => {
  window.dispatchEvent(new CustomEvent(PDF_EXPORT_CLOSE_DROPDOWNS_EVENT));
  document.body.dispatchEvent(
    new MouseEvent("mousedown", { bubbles: true, cancelable: true })
  );
  window.dispatchEvent(
    new KeyboardEvent("keydown", { key: "Escape", bubbles: true, cancelable: true })
  );
  await waitForLayout();
};

const resolveExportElement = (className?: string): HTMLElement | null => {
  if (className && className !== PAGE_EXPORT_TARGET_CLASS) {
    return document.querySelector(`.${className}`) as HTMLElement | null;
  }

  return (
    document.getElementById("root") ||
    (document.querySelector(`.${PAGE_EXPORT_TARGET_CLASS}`) as HTMLElement | null) ||
    (document.querySelector(".dashboard-container") as HTMLElement | null) ||
    document.documentElement
  );
};

const isPageExport = (className?: string) =>
  !className || className === PAGE_EXPORT_TARGET_CLASS;

const createExportOverlay = (message = "Generating PDF…") => {
  const overlay = document.createElement("div");
  overlay.className = "pdf-export-overlay";
  overlay.setAttribute("role", "status");
  overlay.setAttribute("aria-live", "polite");
  overlay.innerHTML = `
    <div class="pdf-export-overlay__panel">
      <div class="pdf-export-overlay__spinner" aria-hidden="true"></div>
      <p class="pdf-export-overlay__message">${message}</p>
    </div>
  `;
  document.body.appendChild(overlay);
  return () => overlay.remove();
};

const copyCanvasContent = (source: HTMLElement, clone: HTMLElement) => {
  const sourceCanvases = source.querySelectorAll("canvas");
  const cloneCanvases = clone.querySelectorAll("canvas");

  cloneCanvases.forEach((cloneCanvas, index) => {
    const sourceCanvas = sourceCanvases[index];
    if (
      !(sourceCanvas instanceof HTMLCanvasElement) ||
      !(cloneCanvas instanceof HTMLCanvasElement)
    ) {
      return;
    }

    cloneCanvas.width = sourceCanvas.width;
    cloneCanvas.height = sourceCanvas.height;
    cloneCanvas.getContext("2d")?.drawImage(sourceCanvas, 0, 0);
  });
};

const prepareClonedDocument = (
  clonedDoc: Document,
  clonedElement: HTMLElement,
  pageExport: boolean
) => {
  const html = clonedDoc.documentElement;
  html.classList.add("pdf-export-clone");
  html.classList.add(pageExport ? "pdf-export-clone--page" : "pdf-export-clone--chart");

  if (pageExport) {
    if (clonedElement) {
      clonedElement.style.boxSizing = "border-box";
      clonedElement.style.overflow = "visible";
    }

    // Curve Learning only: remove logo on exported page per client request
    if (Env.TENANT_ID === "curvelearning") {
      html.classList.add("pdf-export-clone--hide-logo");
      clonedDoc.querySelectorAll<HTMLElement>(".app-logo, .header-logo").forEach((el) => {
        el.style.display = "none";
      });
    }
  }

  // Ensure all tables and their containers expand to render all rows without truncation
  clonedDoc.querySelectorAll<HTMLElement>(".table-graph-scroll-container").forEach((el) => {
    el.style.maxHeight = "none";
    el.style.height = "auto";
    el.style.overflow = "visible";
    el.style.overflowX = "visible";
    el.style.overflowY = "visible";
  });

  clonedDoc.querySelectorAll<HTMLElement>(".chart-container").forEach((el) => {
    if (el.querySelector("table") || el.classList.contains("chart-container--table")) {
      el.classList.add("chart-container--table");
      el.style.maxHeight = "none";
      el.style.height = "auto";
      el.style.minHeight = "420px";
      el.style.overflow = "visible";
    }
  });

  clonedDoc.querySelectorAll<HTMLElement>(".table-graph-scroll-container thead").forEach((thead) => {
    thead.style.position = "static";
  });

  clonedDoc.querySelectorAll<HTMLElement>(".table-graph-scroll-container table").forEach((tbl) => {
    tbl.style.height = "auto";
    tbl.style.maxHeight = "none";
    tbl.style.overflow = "visible";
  });

  clonedDoc.querySelectorAll<HTMLElement>(".dialog-box, .dialog-content").forEach((dialog) => {
    dialog.style.maxHeight = "none";
    dialog.style.height = "auto";
    dialog.style.overflow = "visible";
    dialog.style.top = "0px";
  });

  clonedDoc.querySelectorAll<HTMLElement>(".dashboard-container, .charts-container, .app").forEach((el) => {
    el.style.maxHeight = "none";
    el.style.overflow = "visible";
  });
};

const buildPdfFromCanvas = (canvas: HTMLCanvasElement, fileName: string) => {
  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF("p", "mm", "a4");
  const imgWidth = 210;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  const pageHeight = 297;

  let yPos = 0;
  while (yPos < imgHeight) {
    pdf.addImage(imgData, "PNG", 0, yPos * -1, imgWidth, imgHeight);
    yPos += pageHeight;
    if (yPos < imgHeight) {
      pdf.addPage();
    }
  }

  pdf.save(`${fileName || "page"}.pdf`);
};

const captureElementAsCanvas = async (
  source: HTMLElement,
  pageExport: boolean
): Promise<HTMLCanvasElement> => {
  const exportWidth = pageExport
    ? Math.max(
        source.scrollWidth,
        source.offsetWidth,
        source.clientWidth,
        document.documentElement.scrollWidth,
        document.documentElement.clientWidth,
        window.innerWidth
      )
    : Math.max(source.scrollWidth, source.offsetWidth, source.clientWidth);

  return html2canvas(source, {
    scale: 2,
    width: exportWidth,
    windowWidth: exportWidth,
    windowHeight: Math.max(window.innerHeight * 2, source.scrollHeight * 2, 8000),
    scrollX: 0,
    scrollY: 0,
    useCORS: true,
    allowTaint: true,
    backgroundColor: "#ffffff",
    logging: false,
    onclone: (clonedDoc, clonedElement) => {
      copyCanvasContent(source, clonedElement);
      prepareClonedDocument(clonedDoc, clonedElement, pageExport);
    },
  });
};

const downloadPageAsPDF = async (name?: string, className?: string) => {
  const element = resolveExportElement(className);
  if (!element) {
    console.error(
      className
        ? `Element with class "${className}" not found.`
        : "No export target element found."
    );
    return;
  }

  const pageExport = isPageExport(className);
  const removeOverlay = createExportOverlay();

  try {
    await closeOpenDropdowns();
    if (pageExport) {
      await new Promise<void>((resolve) => setTimeout(resolve, CHART_REDRAW_DELAY_MS));
    }
    await waitForLayout();

    const canvas = await captureElementAsCanvas(element, pageExport);
    buildPdfFromCanvas(canvas, name || "page");
  } finally {
    removeOverlay();
  }
};

export default downloadPageAsPDF;
